/**
 * Copyright(c) Live2D Inc. All rights reserved.
 *
 * Use of this source code is governed by the Live2D Open Software license
 * that can be found at https://www.live2d.com/eula/live2d-open-software-license-agreement_en.html.
 */

import { CubismMatrix44 } from '@framework/math/cubismmatrix44';
import { ACubismMotion } from '@framework/motion/acubismmotion';
import { InvalidMotionQueueEntryHandleValue } from '@framework/motion/cubismmotionqueuemanager';
import { CubismWebGLOffscreenManager } from '@framework/rendering/cubismoffscreenmanager';

import * as LAppDefine from './lappdefine';
import { LAppModel, type DrawableBounds } from './lappmodel';
import { LAppPal } from './lapppal';
import { LAppSubdelegate } from './lappsubdelegate';
import { modelConfigs } from './generated_model_config';
import type { FollowConfig, HideGroupConfig, ModelConfig, MotionButtonConfig, ShowAction, StoryTouchAction } from './generated_model_types';

const unlockedMotionStorageKeyPrefix = 'live2d-unlocked-motions:';
const alwaysUnlockedMotions = new Set(['idle1', 'idle2']);

function currentConfig(sceneIndex: number): ModelConfig {
  return modelConfigs[sceneIndex] ?? modelConfigs[0];
}

function requestedInitialSceneIndex(): number {
  if (typeof window === 'undefined') {
    return 0;
  }

  try {
    const requestedModel = new URLSearchParams(window.location.search).get('model')?.trim();
    if (!requestedModel) {
      return 0;
    }

    const modelIndex = LAppDefine.ModelDir.findIndex(modelDir => modelDir === requestedModel);
    if (modelIndex >= 0) {
      return modelIndex;
    }

    const configIndex = modelConfigs.findIndex(config => {
      return config.id === requestedModel || config.modelDir === requestedModel;
    });
    return configIndex >= 0 ? configIndex : 0;
  } catch {
    return 0;
  }
}

function unlockedMotionStorageKey(modelId: string): string {
  return `${unlockedMotionStorageKeyPrefix}${modelId}`;
}

function readUnlockedMotions(modelId: string): Set<string> {
  if (typeof window === 'undefined') {
    return new Set();
  }

  try {
    const rawValue = window.localStorage.getItem(unlockedMotionStorageKey(modelId));
    const parsedValue = rawValue ? JSON.parse(rawValue) : [];
    return Array.isArray(parsedValue)
      ? new Set(parsedValue.map(item => String(item)))
      : new Set();
  } catch {
    return new Set();
  }
}

function getValidClickShowActions(config: ModelConfig): ShowAction[] {
  const unlockedMotions = readUnlockedMotions(config.id);

  return config.clickShowActions.filter(action => {
    return (
      alwaysUnlockedMotions.has(action.actionId) ||
      !config.unlockableMotions.some(item => item.actionId === action.actionId) ||
      unlockedMotions.has(action.actionId)
    );
  });
}

function findTappedHitArea(
  model: LAppModel,
  config: ModelConfig,
  x: number,
  y: number
): string | null {
  return (config.hitAreaNames || []).find(hitArea => model.hitTest(hitArea, x, y)) || null;
}

function getClickShowActionsForHitArea(
  config: ModelConfig,
  sourceHitArea: string | null
): ShowAction[] {
  return getValidClickShowActions(config).filter(action => {
    if (!action.hitAreaParam) {
      return true;
    }
    return sourceHitArea !== null && action.hitAreas.indexOf(sourceHitArea) >= 0;
  });
}

function chooseWeightedAction(actions: ShowAction[]): ShowAction | null {
  if (actions.length === 0) {
    return null;
  }

  const totalWeight = actions.reduce((sum, action) => sum + action.weight, 0);
  let cursor = Math.random() * totalWeight;

  for (const action of actions) {
    cursor -= action.weight;
    if (cursor <= 0) {
      return action;
    }
  }

  return actions[actions.length - 1];
}

type ManualMotionPlayback = {
  group: string;
  index: number;
  actionId?: string;
  label?: string;
  duration?: number;
  nativeLoop?: boolean;
};

type ManualMotionPlaybackOptions = Partial<Omit<ManualMotionPlayback, 'group' | 'index'>> & {
  loop?: boolean;
  ambientMotion?: MotionButtonConfig | null;
};

export class LAppLive2DManager {
  private releaseAllModel(): void {
    this._models.length = 0;
  }

  public setOffscreenSize(width: number, height: number): void {
    for (let i = 0; i < this._models.length; i++) {
      const model: LAppModel = this._models[i];
      model?.setRenderTargetSize(width, height);
    }
  }

  public onDrag(x: number, y: number): void {
    const model: LAppModel = this._models[0];
    if (model) {
      model.setDragging(x, y);
    }
  }

  public onDragBegin(x: number, y: number): boolean {
    const model: LAppModel = this._models[0];
    if (!model) {
      return false;
    }

    const config = currentConfig(this._sceneIndex);
    const followConfigs = config.followConfigs || [];
    if (followConfigs.length === 0) {
      model.beginLegacyFollow(x, y);
      return true;
    }

    const matchedConfig = followConfigs.find(
      followConfig =>
        Boolean(followConfig.activeName) &&
        model.hitTest(followConfig.activeName, x, y)
    );
    const defaultConfig = followConfigs.find(
      followConfig => !followConfig.activeName
    );
    const followConfig: FollowConfig | undefined =
      matchedConfig || defaultConfig;
    if (!followConfig) {
      model.beginLegacyFollow(x, y);
      window.dispatchEvent(
        new CustomEvent('live2d-legacy-follow-began', {
          detail: { modelId: config.id }
        })
      );
      return true;
    }

    model.beginConfiguredFollow(followConfig, x, y);
    window.dispatchEvent(
      new CustomEvent('live2d-follow-began', {
        detail: {
          modelId: config.id,
          followConfig,
          matchedHitArea: matchedConfig?.activeName || ''
        }
      })
    );
    return true;
  }

  public onDragMove(x: number, y: number): void {
    this._models[0]?.updateFollowDrag(x, y);
  }

  public onDragEnd(): void {
    const endedConfig = this._models[0]?.endFollowDrag();
    if (!endedConfig) {
      return;
    }

    window.dispatchEvent(
      new CustomEvent('live2d-follow-ended', {
        detail: {
          modelId: currentConfig(this._sceneIndex).id,
          followConfig: endedConfig
        }
      })
    );
  }

  public onTap(x: number, y: number): void {
    if (LAppDefine.DebugLogEnable) {
      LAppPal.printMessage(
        `[APP]tap point: {x: ${x.toFixed(2)} y: ${y.toFixed(2)}}`
      );
    }

    const model: LAppModel = this._models[0];

    if (!model) {
      return;
    }

    this.clearManualMotionLoop();

    const config = currentConfig(this._sceneIndex);
    const storyTouchActions = config.storyTouchActions || [];
    if (storyTouchActions.length > 0) {
      const hitAreas: string[] = [];
      storyTouchActions.forEach((action: StoryTouchAction) => {
        const actionHitAreas = action.sourceHitAreas?.length ? action.sourceHitAreas : [action.hitArea];
        actionHitAreas.forEach(hitArea => {
          if (hitAreas.indexOf(hitArea) < 0) {
            hitAreas.push(hitArea);
          }
        });
      });
      const sourceHitArea = hitAreas.find(hitArea => model.hitTest(hitArea, x, y));
      if (sourceHitArea) {
        const candidates = storyTouchActions.filter(action => {
          const actionHitAreas = action.sourceHitAreas?.length ? action.sourceHitAreas : [action.hitArea];
          return actionHitAreas.indexOf(sourceHitArea) >= 0;
        });
        const action = candidates[Math.floor(Math.random() * candidates.length)] as StoryTouchAction | undefined;
        if (action) {
          window.dispatchEvent(
            new CustomEvent('live2d-story-touch-action', {
              detail: { ...action, modelId: config.id, sourceHitArea }
            })
          );
        }
      }
      return;
    }

    const hasStoryCues = (config.storySegments || []).some(segment => segment.cues.length > 0);
    if (hasStoryCues) {
      window.dispatchEvent(
        new CustomEvent('live2d-story-tap', {
          detail: { modelId: config.id }
        })
      );
      return;
    }

    const sourceHitArea = findTappedHitArea(model, config, x, y);
    const candidates = getClickShowActionsForHitArea(config, sourceHitArea);
    const action = chooseWeightedAction(candidates);

    if (!action) {
      return;
    }

    const motionHandle = model.startMotion(
      action.group,
      action.index,
      LAppDefine.PriorityForce,
      this.finishedMotion,
      this.beganMotion
    );

    if (motionHandle === InvalidMotionQueueEntryHandleValue) {
      return;
    }

    this.syncAmbientMotion(this.ambientMotionForAction(config, action));

    window.dispatchEvent(
      new CustomEvent('live2d-show-action-began', {
        detail: {
          ...action,
          modelId: config.id,
          sourceHitArea,
          candidateCount: candidates.length,
          hitAreaCandidateCount: candidates.filter(candidate => candidate.hitAreaParam).length
        }
      })
    );
  }

  public playMotion(
    group: string,
    no: number,
    options: ManualMotionPlaybackOptions = {}
  ): void {
    const detail: ManualMotionPlayback = {
      group,
      index: no,
      actionId: options.actionId,
      label: options.label,
      duration: options.duration,
      nativeLoop: options.nativeLoop === true
    };

    this._currentManualMotion = detail;
    this._loopMotion = options.loop && !options.nativeLoop ? detail : null;
    if (this.startManualMotion(detail)) {
      this.syncAmbientMotion(this.ambientMotionForAction(currentConfig(this._sceneIndex), detail, options.ambientMotion));
    }
  }

  public setManualMotionLoop(enabled: boolean, detail?: ManualMotionPlayback): void {
    if (!enabled) {
      this._loopMotion = null;
      return;
    }

    if (detail) {
      this._currentManualMotion = detail;
      this._loopMotion = detail;
      return;
    }

    if (this._currentManualMotion) {
      this._loopMotion = this._currentManualMotion;
    }
  }

  public setHiddenGroups(groups: HideGroupConfig[]): void {
    this._hiddenGroups = Array.isArray(groups) ? groups : [];
    this._models[0]?.setHiddenGroups(this._hiddenGroups);
  }

  public getCurrentModelCanvasSize(): { width: number; height: number; pixelsPerUnit: number; pixelWidth: number; pixelHeight: number } | null {
    const cubismModel = this._models[0]?.getModel();
    if (!cubismModel) {
      return null;
    }

    const width = cubismModel.getCanvasWidth();
    const height = cubismModel.getCanvasHeight();
    const pixelsPerUnit = cubismModel.getPixelsPerUnit();
    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      !Number.isFinite(pixelsPerUnit) ||
      width <= 0 ||
      height <= 0 ||
      pixelsPerUnit <= 0
    ) {
      return null;
    }

    return {
      width,
      height,
      pixelsPerUnit,
      pixelWidth: width * pixelsPerUnit,
      pixelHeight: height * pixelsPerUnit
    };
  }

  public getCurrentDrawableBounds(useModelMatrix: boolean = true): DrawableBounds | null {
    return this._models[0]?.getCurrentDrawableBounds(useModelMatrix) || null;
  }

  public returnToIdle(): void {
    const model: LAppModel = this._models[0];

    if (!model) {
      return;
    }

    this.clearManualMotionLoop();

    window.dispatchEvent(
      new CustomEvent('live2d-motion-cancelled', {
        detail: { group: LAppDefine.MotionGroupIdle, index: 0 }
      })
    );

    const motionHandle = model.startMotion(
      LAppDefine.MotionGroupIdle,
      0,
      LAppDefine.PriorityForce,
      this.finishedMotion,
      this.beganMotion
    );

    if (motionHandle !== InvalidMotionQueueEntryHandleValue) {
      this.syncAmbientMotion(
        this.ambientMotionForAction(currentConfig(this._sceneIndex), {
          group: LAppDefine.MotionGroupIdle,
          index: 0,
          actionId: 'idle',
          label: 'Idle'
        })
      );
    }
  }

  public onUpdate(): void {
    const gl = this._subdelegate.getGl();
    CubismWebGLOffscreenManager.getInstance().beginFrameProcess(gl);

    const { width, height } = this._subdelegate.getCanvas();
    const projection: CubismMatrix44 = new CubismMatrix44();
    const model: LAppModel = this._models[0];

    if (model.getModel()) {
      if (model.getModel().getCanvasWidth() > 1.0 && width < height) {
        model.getModelMatrix().setWidth(2.0);
        projection.scale(1.0, width / height);
      } else {
        projection.scale(height / width, 1.0);
      }

      if (this._viewMatrix != null) {
        projection.multiplyByMatrix(this._viewMatrix);
      }
    }

    model.update();
    model.draw(projection);
    CubismWebGLOffscreenManager.getInstance().endFrameProcess(gl);
    CubismWebGLOffscreenManager.getInstance().releaseStaleRenderTextures(gl);
  }

  public nextScene(): void {
    const no: number = (this._sceneIndex + 1) % LAppDefine.ModelDirSize;
    this.changeScene(no);
  }

  public setScene(index: number): void {
    if (index < 0 || index >= LAppDefine.ModelDirSize) {
      return;
    }

    this.changeScene(index);
  }

  private changeScene(index: number): void {
    this.clearManualMotionLoop();
    this.clearAmbientMotion();
    this._sceneIndex = index;

    if (LAppDefine.DebugLogEnable) {
      LAppPal.printMessage(`[APP]model index: ${this._sceneIndex}`);
    }

    const model: string = LAppDefine.ModelDir[index];
    const modelPath: string = LAppDefine.ResourcesPath + model + '/';
    let modelJsonName: string = LAppDefine.ModelDir[index];
    modelJsonName += '.model3.json';

    this.releaseAllModel();
    const instance = new LAppModel();
    instance.setSubdelegate(this._subdelegate);
    instance.setHiddenGroups(this._hiddenGroups);
    instance.loadAssets(modelPath, modelJsonName);
    this._models.push(instance);
    this.dispatchModelConfig();
  }

  private dispatchModelConfig(): void {
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('live2d-model-config-changed', {
        detail: {
          sceneIndex: this._sceneIndex,
          models: modelConfigs.map(({ id, title, assetKind, categoryLabel, modelDir, styleId, styleName, stage, variantKey, variantLabel, variantSourceLayer }) => ({ id, title, assetKind, categoryLabel, modelDir, styleId, styleName, stage, variantKey, variantLabel, variantSourceLayer })),
          config: currentConfig(this._sceneIndex)
        }
      }));
    }, 0);
  }

  public setViewMatrix(m: CubismMatrix44): void {
    for (let i = 0; i < 16; i++) {
      this._viewMatrix.getArray()[i] = m.getArray()[i];
    }
  }

  public addModel(sceneIndex: number = 0): void {
    this._sceneIndex = sceneIndex;
    this.changeScene(this._sceneIndex);
  }

  public constructor() {
    this._subdelegate = null;
    this._viewMatrix = new CubismMatrix44();
    this._models = new Array<LAppModel>();
    this._sceneIndex = requestedInitialSceneIndex();
    this._hiddenGroups = [];
    this._currentManualMotion = null;
    this._loopMotion = null;
    this._ambientMotion = null;
  }

  public release(): void {}

  public initialize(subdelegate: LAppSubdelegate): void {
    this._subdelegate = subdelegate;
    this.changeScene(this._sceneIndex);
  }

  private _subdelegate: LAppSubdelegate;
  _viewMatrix: CubismMatrix44;
  _models: Array<LAppModel>;
  private _sceneIndex: number;
  private _hiddenGroups: HideGroupConfig[];
  private _currentManualMotion: ManualMotionPlayback | null;
  private _loopMotion: ManualMotionPlayback | null;
  private _ambientMotion: ManualMotionPlayback | null;

  private clearManualMotionLoop(): void {
    this._currentManualMotion = null;
    this._loopMotion = null;
  }

  private clearAmbientMotion(): void {
    this._ambientMotion = null;
    this._models[0]?.stopAmbientMotion();
  }

  private sameMotion(left: Partial<ManualMotionPlayback>, right: Partial<ManualMotionPlayback>): boolean {
    if (left.actionId && right.actionId && left.actionId === right.actionId) {
      return true;
    }
    return left.group === right.group && left.index === right.index;
  }

  private ambientMotionForAction(
    config: ModelConfig,
    primary?: Partial<ManualMotionPlayback>,
    override?: MotionButtonConfig | null
  ): ManualMotionPlayback | null {
    const source = override === undefined ? config.ambientMotion : override;
    if (!source) {
      return null;
    }

    const detail: ManualMotionPlayback = {
      group: source.group,
      index: source.index,
      actionId: source.actionId,
      label: source.label,
      duration: source.duration,
      nativeLoop: true
    };

    if (primary && this.sameMotion(detail, primary)) {
      return null;
    }

    return detail;
  }

  private syncAmbientMotion(detail: ManualMotionPlayback | null): void {
    const model: LAppModel = this._models[0];
    if (!model) {
      this._ambientMotion = detail;
      return;
    }

    if (!detail) {
      this.clearAmbientMotion();
      return;
    }

    if (this._ambientMotion && this.sameMotion(this._ambientMotion, detail)) {
      return;
    }

    if (model.startAmbientMotion(detail.group, detail.index, true)) {
      this._ambientMotion = detail;
      return;
    }

    this.clearAmbientMotion();
  }

  private startManualMotion(detail: ManualMotionPlayback): boolean {
    const model: LAppModel = this._models[0];

    if (!model) {
      return false;
    }

    const motionHandle = model.startMotion(
      detail.group,
      detail.index,
      LAppDefine.PriorityForce,
      detail.nativeLoop ? undefined : this.finishedMotion,
      this.beganMotion,
      detail.nativeLoop === true
    );

    if (motionHandle === InvalidMotionQueueEntryHandleValue) {
      return false;
    }

    window.dispatchEvent(
      new CustomEvent('live2d-motion-began', {
        detail
      })
    );

    return true;
  }

  beganMotion = (self: ACubismMotion): void => {
    LAppPal.printMessage('Motion Began:');
    console.log(self);
  };

  finishedMotion = (self: ACubismMotion): void => {
    LAppPal.printMessage('Motion Finished:');
    console.log(self);

    if (this._loopMotion) {
      if (this.startManualMotion(this._loopMotion)) {
        return;
      }

      this.clearManualMotionLoop();
    } else {
      this._currentManualMotion = null;
    }

    window.dispatchEvent(new CustomEvent('live2d-motion-finished'));
  };
}
